import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid2 as Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  GridView as GridViewIcon,
  ViewList as ListViewIcon,
  AttachFile as AttachIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';
import { formatDate } from '../../utils/formatters';
import { usePersonNotes } from '../../hooks/queries/usePersonDetail';
import useThemeLabels from '../../hooks/useThemeLabels';
import useDialog from '../../hooks/useDialog';
import LoadingState from '../ui/LoadingState';
import StandardCard from '../ui/StandardCard';
import PersonNoteDialog from './PersonNoteDialog';

export default function PersonNotesSection({ personId, ownerUserId }) {
  const { getLabel } = useThemeLabels();
  const label = getLabel('personRecords', 'Anotações');
  const labelSingular = getLabel('personRecordsSingular', 'Anotação');

  const [viewMode, setViewMode] = useState(() => localStorage.getItem('person_notes_view_mode') || 'list');
  const { notes, loading, removeAsync, isRemoving } = usePersonNotes(personId);
  const noteDialog = useDialog();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleViewModeChange = (_, mode) => {
    if (!mode) return;
    setViewMode(mode);
    localStorage.setItem('person_notes_view_mode', mode);
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await removeAsync(confirmDeleteId);
    } finally {
      setConfirmDeleteId(null);
    }
  };

  if (loading) return <LoadingState message="Carregando..." />;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotesIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
            <Tooltip title="Lista"><ToggleButton value="list"><ListViewIcon fontSize="small" /></ToggleButton></Tooltip>
            <Tooltip title="Cards"><ToggleButton value="cards"><GridViewIcon fontSize="small" /></ToggleButton></Tooltip>
          </ToggleButtonGroup>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => noteDialog.handleOpen(null)}>
            Nova {labelSingular}
          </Button>
        </Box>
      </Box>

      {notes.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Nenhuma {labelSingular.toLowerCase()} registrada. Clique em "Nova {labelSingular}" para começar.
        </Typography>
      )}

      {/* Lista (acordeon) */}
      {viewMode === 'list' && notes.map((note) => (
        <Accordion key={note.id} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
              <Typography variant="body2" fontWeight="bold" noWrap sx={{ flexGrow: 1 }}>{note.title}</Typography>
              {note.attachments?.length > 0 && (
                <Chip icon={<AttachIcon />} label={note.attachments.length} size="small" sx={{ height: 20 }} />
              )}
              <Typography variant="caption" color="text.secondary">{formatDate(note.createdAt)}</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {note.body && (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{note.body}</Typography>
            )}
            {note.attachments?.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {note.attachments.map((a) => (
                  <Chip key={a.id} icon={<AttachIcon />} label={a.fileName} size="small"
                    component="a" href={a.url} target="_blank" rel="noopener" clickable />
                ))}
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
              <IconButton size="small" color="primary" onClick={() => noteDialog.handleOpen(note)} title="Editar">
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => setConfirmDeleteId(note.id)} title="Excluir">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Cards */}
      {viewMode === 'cards' && (
        <Grid container spacing={1.5}>
          {notes.map((note) => (
            <Grid size={{ xs: 12, sm: 6 }} key={note.id}>
              <StandardCard onClick={() => noteDialog.handleOpen(note)} sx={{ cursor: 'pointer', p: 1.5, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" fontWeight="bold" noWrap sx={{ flexGrow: 1 }}>{note.title}</Typography>
                  {note.attachments?.length > 0 && (
                    <Chip icon={<AttachIcon />} label={note.attachments.length} size="small" sx={{ height: 18 }} />
                  )}
                </Box>
                {note.body && (
                  <Typography variant="caption" color="text.secondary" sx={{
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {note.body}
                  </Typography>
                )}
                <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                  {formatDate(note.createdAt)}
                </Typography>
              </StandardCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog criar/editar */}
      <PersonNoteDialog
        open={noteDialog.open}
        onClose={noteDialog.handleClose}
        personId={personId}
        ownerUserId={ownerUserId}
        noteId={noteDialog.data?.id || null}
        labelSingular={labelSingular}
      />

      {/* Confirmação de exclusão */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir {labelSingular.toLowerCase()}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Esta ação não pode ser desfeita. Os anexos também serão removidos.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={isRemoving}>
            {isRemoving ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
